import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import useCapabilities, { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import type { Section } from "@/types";
import { resolveDefaultRosterSectionId, resolveRosterAccessibleSections } from "../resolveAccessibleSections";

type UseRosterAccessibleSectionsOptions = {
  enabled?: boolean;
};

export function useRosterAccessibleSections(
  preferredSectionId?: string | null,
  options?: UseRosterAccessibleSectionsOptions
): {
  sections: Section[];
  defaultSectionId: string | null;
  canViewRosterActivity: boolean;
  loading: boolean;
} {
  const enabled = options?.enabled ?? true;
  const { hasRole, loaded: capabilitiesLoaded, sectionLegacyIds, sectionLegacyIdsLoading } = useCapabilities();
  const canViewSection = useHasCapabilityBool({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewSection });

  const isSectionStaff = capabilitiesLoaded && (hasRole(ROLE.SECTION_STAFF) || hasRole(ROLE.SECTION_ADMIN));

  const shouldLoadSections = enabled && capabilitiesLoaded && canViewSection;

  const { data: sectionsData, loading: sectionsLoading } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    skip: !shouldLoadSections
  });

  const sections = useMemo(() => {
    if (!enabled) {
      return [];
    }

    return resolveRosterAccessibleSections({
      canViewSection,
      isSectionStaff,
      assignedSectionIds: sectionLegacyIds,
      allSections: sectionsData?.sections ?? []
    });
  }, [enabled, canViewSection, isSectionStaff, sectionLegacyIds, sectionsData?.sections]);

  const defaultSectionId = useMemo(
    () => resolveDefaultRosterSectionId(sections, preferredSectionId),
    [sections, preferredSectionId]
  );

  const loading = !enabled
    ? false
    : !capabilitiesLoaded || (shouldLoadSections && sectionsLoading) || (isSectionStaff && sectionLegacyIdsLoading);

  return {
    sections,
    defaultSectionId,
    canViewRosterActivity: canViewSection && sections.length > 0,
    loading
  };
}
